import { ReactNode, useMemo, useState } from "react";

import { KhRepoKnowledgeItemRow } from "../models/kh-repo.models";

type KhRepoExpandableTextProps = {
  rawContent: KhRepoKnowledgeItemRow[];
  formattedRawContent: (items: KhRepoCollapsedItem[]) => ReactNode;
  title?: string;
  collapsedCharLimit?: number;
  displayVariant?: "card" | "inline";
};

const DEFAULT_CHAR_LIMIT = 200;

export type KhRepoCollapsedItem = KhRepoKnowledgeItemRow & { partial?: boolean };

const KhRepoExpandableText = ({
  rawContent = [],
  formattedRawContent = () => null,
  title = "Details",
  collapsedCharLimit = DEFAULT_CHAR_LIMIT,
  displayVariant = "card",
}: KhRepoExpandableTextProps) => {
  const [expanded, setExpanded] = useState(false);
  const isInline = displayVariant === "inline";

  const totalLength = useMemo(
    () => rawContent.reduce((sum, item) => sum + (item.content?.length ?? 0), 0),
    [rawContent],
  );
  const shouldTruncate = totalLength > collapsedCharLimit;

  const visibleItems: KhRepoCollapsedItem[] = useMemo(() => {
    if (!shouldTruncate || expanded) {
      return rawContent;
    }

    const truncated: KhRepoCollapsedItem[] = [];
    let accumulated = 0;

    for (const item of rawContent) {
      const contentLength = item.content?.length ?? 0;

      if (accumulated + contentLength <= collapsedCharLimit) {
        truncated.push(item);
        accumulated += contentLength;
        continue;
      }

      const remaining = collapsedCharLimit - accumulated;
      if (remaining > 0 && item.content) {
        truncated.push({
          ...item,
          content: `${item.content.slice(0, remaining).trimEnd()}...`,
          partial: true,
        });
      }
      break;
    }

    return truncated;
  }, [collapsedCharLimit, expanded, rawContent, shouldTruncate]);

  if (!rawContent.length) {
    return null;
  }

  const renderBody = () => (
    <>
      {formattedRawContent(visibleItems)}
      {shouldTruncate ? (
        <button
          type="button"
          className="mt-3 text-sm font-semibold tracking-wide text-slate-700"
          onClick={() => setExpanded((prev) => !prev)}
          aria-expanded={expanded}
        >
          {expanded ? "Show less" : "Show more"}
        </button>
      ) : null}
    </>
  );

  if (isInline) {
    return (
      <div className="flex flex-col">
        {title ? <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-600">{title}</div> : null}
        {renderBody()}
      </div>
    );
  }

  return (
    <div className="mt-3 flex">
      <div className="w-full rounded-lg border border-slate-400 bg-white shadow-sm">
        {title ? (
          <div className="border-b border-slate-300 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-slate-600">
            {title}
          </div>
        ) : null}
        <div className="px-5 py-4">{renderBody()}</div>
      </div>
    </div>
  );
};

export default KhRepoExpandableText;
